import { useState, useCallback, useEffect } from "react";
import useLocalStorageState from "@/modules/common/hooks/local-storage.hook";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";

interface UseCommonTableHeaderProps {
  tableName: string | undefined;
  initialSortState: { [key: string]: string } | undefined;
  setInitialSortState: ((sortState: {}) => void) | undefined;
  clearSortTrigger?: boolean;
  columns: any;
}

export const useCommonTableHeader = ({
  tableName,
  initialSortState,
  setInitialSortState,
  clearSortTrigger,
  columns,
}: UseCommonTableHeaderProps) => {
  const storedOrgName = localStorage.getItem("selectedOrganizationName");

  const [localStorageSort, setLocalStorageSort] = useLocalStorageState(
    `${tableName}TableSortState${storedOrgName}`,
    {}
  );
  const [sortState, setSortState] = useState<{
    [key: string]: string;
  }>(initialSortState || {});

  const updateSortState = useCallback(
    (newState: any) => {
      setSortState(newState);
      setLocalStorageSort(newState);
      setInitialSortState && setInitialSortState(newState);
    },
    [setInitialSortState, setLocalStorageSort]
  );

  useEffect(() => {
    if (JSON.stringify(initialSortState) !== JSON.stringify(sortState)) {
      setSortState(initialSortState || {});
      setLocalStorageSort(initialSortState || {});
    }
  }, [initialSortState, setLocalStorageSort]);

  useEffect(() => {
    if (JSON.stringify(localStorageSort) !== JSON.stringify(sortState)) {
      updateSortState(localStorageSort);
    }
  }, [localStorageSort, sortState, updateSortState]);

  const handleSort = useCallback(
    (column: any) => {
      if (column.isSortable) {
        updateSortState((prevState: any) => {
          const currentDirection =
            prevState && prevState[column.id] ? prevState[column.id] : "";
          let newDirection = "";
          switch (currentDirection) {
            case "":
              newDirection = "DESC";
              break;
            case "DESC":
              newDirection = "ASC";
              break;
            case "ASC":
              newDirection = "";
              break;
            default:
              break;
          }

          if (column.onSort) {
            column.onSort(
              column.sortKey ? column.sortKey : column.filterKey,
              newDirection
            );
          }

          const newSortState = { ...prevState };
          if (newDirection === "") {
            delete newSortState[column.id];
          } else {
            newSortState[column.id] = newDirection;
          }

          return newSortState;
        });
      }
    },
    [updateSortState]
  );

  useEffect(() => {
    if (clearSortTrigger) {
      updateSortState({});
      columns.forEach((column: any) => {
        column.toggleSortBy(false);
      });
    }
  }, [clearSortTrigger]);

  const getSortIcon = useCallback(
    (column: { id: string; isSortable: boolean }) => {
      if (!column.isSortable) return null;
      const sortDirection = sortState[column.id];
      if (sortDirection === "DESC") {
        return <ChevronDown style={{ width: 16, height: 16 }} />;
      } else if (sortDirection === "ASC") {
        return <ChevronUp style={{ width: 16, height: 16 }} />;
      }
      return <ChevronsUpDown style={{ width: 16, height: 16 }} />;
    },
    [sortState]
  );

  return {
    sortState,
    handleSort,
    getSortIcon,
  };
};
